import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ShoppingCart, Zap, ArrowLeft, Minus, Plus, ShieldCheck, Download, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import { purchaseDetails } from "@/lib/purchases";

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem, openCart } = useCart();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [variantIdx, setVariantIdx] = useState(0);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    api.get(`/shop/products/${id}`).then((r) => setProduct(r.data)).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-32 text-center">
        <p className="text-brown-black/60" data-testid="product-not-found">Produit introuvable.</p>
        <Link to="/boutique" data-testid="product-not-found-back" className="text-gold font-semibold hover:underline mt-4 inline-block">Retour à la boutique</Link>
      </div>
    );
  }
  if (!product) return <div className="min-h-[60vh] flex items-center justify-center text-brown-black/50">Chargement…</div>;

  const hasVariants = product.variants && product.variants.length > 0;
  const variant = hasVariants ? product.variants[variantIdx] : null;
  const price = variant ? Number(variant.price) : Number(product.price);
  const images = product.images && product.images.length ? product.images : [""];
  const purchase = purchaseDetails(product);
  const isExternal = purchase.external;
  const purchasable = isExternal || (purchase.onsite && (Boolean(variant ? variant.file : product.digital_file) || (hasVariants && product.variants.some((v) => v.file))));

  const doAdd = () => {
    if (!purchase.onsite || !purchasable) return false;
    addItem(product, { variant, qty });
    return true;
  };
  const handleAdd = () => { doAdd(); toast.success("Ajouté au panier", { description: product.name }); openCart(); };
  const handleBuy = () => { doAdd(); navigate("/checkout"); };

  return (
    <div className="bg-ivory">
      <Seo title={`${product.name} — Boutique`} description={product.description} path={`/boutique/${product.id}`} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">
        <button onClick={() => navigate("/boutique")} className="inline-flex items-center gap-2 text-brown-black/60 hover:text-gold text-sm font-semibold mb-8" data-testid="product-back">
          <ArrowLeft size={16} /> Retour à la boutique
        </button>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
          {/* Gallery */}
          <div>
            <div className="rounded-2xl overflow-hidden bg-[#0a0e1a] border border-gold/20 aspect-[4/3]">
              {images[activeImg] && <img src={images[activeImg]} alt={product.name} className="w-full h-full object-cover" data-testid="product-main-image" />}
            </div>
            {images.length > 1 && (
              <div className="flex gap-3 mt-4">
                {images.map((im, i) => (
                  <button key={i} data-testid={`product-thumbnail-${i}`} onClick={() => setActiveImg(i)} className={`w-20 h-16 rounded-lg overflow-hidden border-2 transition-colors ${i === activeImg ? "border-gold" : "border-transparent"}`}>
                    <img src={im} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">{product.category}</span>
            <h1 className="font-heading text-3xl md:text-4xl font-light text-brown-black mt-2 leading-tight" data-testid="product-title">{product.name}</h1>
            <div className="flex items-center gap-3 mt-4">
              <span className="font-heading text-3xl font-semibold text-gold" data-testid="product-price">{price.toFixed(2)} €</span>
              {product.old_price > 0 && product.old_price > price && (
                <span className="text-lg text-brown-black/40 line-through">{Number(product.old_price).toFixed(2)} €</span>
              )}
            </div>

            <p data-testid="product-availability" className={`inline-flex items-center gap-2 mt-4 text-sm font-semibold ${isExternal ? "text-blue-600" : purchasable ? "text-emerald-600" : "text-amber-600"}`}>
              {isExternal ? <ExternalLink size={15} /> : <Download size={15} />}
              {isExternal ? (purchase.physical ? "Livre papier — commande via Amazon" : purchase.provider === "amazon" ? "E-book — disponible sur Amazon Kindle" : "Produit numérique — disponible sur Gumroad") : purchasable ? "Produit numérique — disponible immédiatement" : "Bientôt disponible"}
            </p>

            <p className="text-brown-black/70 leading-relaxed mt-6 whitespace-pre-line">{product.description}</p>

            {hasVariants && (
              <div className="mt-7">
                <p className="text-xs uppercase tracking-[0.12em] text-brown-dark/60 font-semibold mb-2.5">Format</p>
                <div className="flex flex-wrap gap-3">
                  {product.variants.map((v, i) => (
                    <button
                      key={v.label}
                      onClick={() => setVariantIdx(i)}
                      data-testid={`product-variant-${i}`}
                      className={`px-4 py-2.5 rounded-full text-sm font-semibold border transition-all ${i === variantIdx ? "bg-gold text-white border-gold" : "bg-white text-brown-dark border-gold/30 hover:border-gold"}`}
                    >
                      {v.label} — {Number(v.price).toFixed(2)} €
                    </button>
                  ))}
                </div>
              </div>
            )}

            {purchase.onsite && (
              <div className="mt-7 flex items-center gap-4">
                <p className="text-xs uppercase tracking-[0.12em] text-brown-dark/60 font-semibold">Quantité</p>
                <div className="flex items-center gap-3">
                  <button data-testid="product-qty-decrease" onClick={() => setQty((n) => Math.max(1, n - 1))} className="w-9 h-9 flex items-center justify-center rounded-full border border-gold/30 text-gold hover:bg-gold/10"><Minus size={14} /></button>
                  <span className="text-lg font-semibold w-6 text-center" data-testid="product-qty">{qty}</span>
                  <button data-testid="product-qty-increase" onClick={() => setQty((n) => n + 1)} className="w-9 h-9 flex items-center justify-center rounded-full border border-gold/30 text-gold hover:bg-gold/10"><Plus size={14} /></button>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              {isExternal ? (
                <a href={purchase.url} target="_blank" rel="noopener noreferrer" data-testid="product-external-link" className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-gold text-white font-semibold hover:bg-gold-light transition-colors">
                  <ExternalLink size={17} className="shrink-0" /> {purchase.label}
                </a>
              ) : (
                <>
                  <button onClick={handleAdd} disabled={!purchasable} data-testid="product-add-to-cart" className={`flex-1 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full border font-semibold transition-colors ${purchasable ? "border-gold text-gold hover:bg-gold/10" : "border-brown-dark/20 text-brown-dark/40 cursor-not-allowed"}`}>
                    <ShoppingCart size={17} /> Ajouter au panier
                  </button>
                  <button onClick={handleBuy} disabled={!purchasable} data-testid="product-buy-now" className={`flex-1 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-semibold transition-colors ${purchasable ? "bg-gold text-white hover:bg-gold-light" : "bg-brown-dark/10 text-brown-dark/40 cursor-not-allowed"}`}>
                    <Zap size={17} /> Acheter maintenant
                  </button>
                </>
              )}
            </div>
            {purchase.onsite && (
              <p className="flex items-center gap-2 text-xs text-brown-black/55 mt-4">
                <ShieldCheck size={14} className="text-gold" /> Paiement 100 % sécurisé par PayPal. Téléchargement immédiat après paiement.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
